import { Box, Icon, Typography, useTheme } from "@outschool/backpack";
import { Activity } from "@outschool/gql-backend-generated";
import { IconDefinition, faBirthdayCake, faClock } from "@outschool/icons";
import {
  getClassTypeIcon,
  useActivityString,
  usePriceDetailsString,
  useSelfPacedIconInfo,
} from "@outschool/ui-components-website";
import React from "react";

import { useEsaUserWithoutPaidMembership } from "../../hooks/useEsaUserWithoutPaidMembership";
import { FreeForMembersChip } from "../Chips/FreeForMembersChip";
import { StateFundingAcceptedChip } from "../Chips/StateFundingAcceptedChip";
import { CoinIcon } from "../subscriptions/CoinIcon";

export type SearchActivityType = Pick<
  Activity,
  | "uid"
  | "user_uid"
  | "isClub"
  | "is_ongoing_weekly"
  | "size_min"
  | "size_max"
  | "hasTeacherSchedule"
  | "duration_weeks"
  | "weekly_meetings"
  | "duration_minutes"
  | "allows_recurring_payment"
  | "price_cents"
  | "age_min"
  | "age_max"
  | "subject"
  | "canEnroll"
  | "isFlexSchedule"
  | "isSelfPaced"
  | "isIncludedWithMembership"
> &
  Partial<Pick<Activity, "clubSection" | "priceCreditsPerMeeting">>;

interface PriceDetailsProps {
  activity: SearchActivityType;
  /** Display pricing in credits instead of currency */
  showCredits?: boolean;
  showFundingBadge?: boolean;
}

function IconBullet({
  icon,
  text,
}: {
  icon: IconDefinition;
  text: string | null;
}) {
  const fixLineHeight = text?.search(/Group|1\-on\-1/) !== -1;
  return (
    <Box flex sx={{ textAlign: "left" }}>
      <Icon
        icon={icon}
        sx={{
          width: "1.25em",
          marginRight: "0.5em",
        }}
      />
      <Typography
        sx={{
          lineHeight: fixLineHeight ? "1em" : "inherit",
        }}
        variant={"caption"}
      >
        {text}
      </Typography>
    </Box>
  );
}

// TODO: This is only used by desktop listings, move into the DesktopListing component
export default function ListingDetails({
  activity,
  showCredits,
  showFundingBadge = false,
}: PriceDetailsProps) {
  const { ageRangeString } = useActivityString();
  const {
    perClassString,
    classTypeString,
    perMeetingPriceString,
    detailedScheduleString,
  } = usePriceDetailsString();
  const classTypeIcon = getClassTypeIcon(activity);
  const selfPacedIconInfo = useSelfPacedIconInfo(activity);
  const theme = useTheme();
  const { isEsaUserWithoutPaidMembership } = useEsaUserWithoutPaidMembership();

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        width: "100%",
        paddingLeft: "24px",
        paddingRight: "16px",
        paddingY: "16px",
      }}
    >
      <Box flex sx={{ paddingBottom: "4px" }}>
        <Box
          flex
          sx={{
            borderBottom: "1px solid #cccccc",
            width: "100%",
            gap: "0.5em",
            paddingRight: "1rem",
          }}
        >
          <Typography variant="display3" emphasized color={"grey.900"}>
            {showCredits ? (
              <Box sx={{ display: "flex" }}>
                <Box sx={{ paddingTop: "0.25rem", paddingRight: "0.5rem" }}>
                  <CoinIcon
                    fillColor={theme.palette.warning?.main!}
                    strokeColor={theme.palette.warning?.[700]!}
                    width={20}
                    height={20}
                  />
                </Box>
                {activity.isIncludedWithMembership &&
                !isEsaUserWithoutPaidMembership
                  ? "0"
                  : activity.priceCreditsPerMeeting}
              </Box>
            ) : (
              perMeetingPriceString(activity)
            )}
          </Typography>
          <Typography variant="caption" sx={{ alignSelf: "center" }}>
            {showCredits ? "credits " : ""}
            {perClassString(activity)}
          </Typography>
        </Box>
      </Box>
      <Box
        flex
        sx={{
          paddingTop: "8px",
          flexDirection: "column",
          height: "100%",
          alignItems: "start",
          gap: "8px",
        }}
      >
        {showFundingBadge && (
          <StateFundingAcceptedChip sx={{ width: "100%" }} />
        )}
        {activity.isIncludedWithMembership && (
          <FreeForMembersChip sx={{ width: "100%" }} />
        )}
        <IconBullet icon={classTypeIcon} text={classTypeString(activity)} />
        <IconBullet icon={faBirthdayCake} text={ageRangeString(activity)} />
        {activity.isSelfPaced ? (
          <IconBullet
            icon={selfPacedIconInfo.weeksOfFeedback.icon}
            text={selfPacedIconInfo.weeksOfFeedback.text}
          />
        ) : (
          <IconBullet icon={faClock} text={detailedScheduleString(activity)} />
        )}
      </Box>
    </Box>
  );
}
